import { Box, Flex, IconButton, Textarea, useColorModeValue, VStack } from '@chakra-ui/react'
import { Send } from 'lucide-react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAgents } from '../hooks/agents/useAgents'

interface QuestionInputProps {
  onSend: (message: string) => void
  onTyping?: () => void
  onStopTyping?: () => void
  disabled?: boolean
  placeholder?: string
  clearOnSend?: boolean
  hasDocumentsInConversation?: boolean // New prop to indicate if conversation contains documents
}

export const QuestionInput: React.FC<QuestionInputProps> = ({
  onSend,
  onTyping,
  onStopTyping,
  disabled,
  placeholder,
  clearOnSend,
  hasDocumentsInConversation = false,
}) => {
  const [question, setQuestion] = useState<string>('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const { currentAgent } = useAgents()
  const { t } = useTranslation()

  // Color mode values
  const inputBg = useColorModeValue('white', 'gray.700')
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const placeholderColor = useColorModeValue('gray.500', 'gray.400')
  const textColor = useColorModeValue('gray.800', 'white')

  const MAX_QUESTION_LENGTH = 30000

  // Auto-resize textarea based on content
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
    }
  }, [question])


  useEffect(() => {
    if (currentAgent && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [currentAgent]);



  // Handle typing events for real-time typing indicators
  useEffect(() => {
    if (question && onTyping) {
      onTyping()
    } else if (!question && onStopTyping) {
      onStopTyping()
    }
  }, [question, onTyping, onStopTyping])

  const handleSend = useCallback(() => {
    if (!question.trim() || disabled) return

    onSend(question)

    if (clearOnSend) {
      setQuestion('')
    }
  }, [
    disabled,
    question,
    onSend,
    clearOnSend,
  ])


  const onEnterPress = useCallback(
    (ev: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (ev.key !== 'Enter' || ev.nativeEvent.isComposing) {
        return
      }
      if (ev.key === 'Enter' && !ev.shiftKey) {
        ev.preventDefault()
        handleSend()
      }
    },
    [handleSend],
  )

  const onQuestionChange = useCallback(
    (ev: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = ev.target.value

      if (!newValue) {
        setQuestion('')
      } else if (newValue.length <= MAX_QUESTION_LENGTH) {
        setQuestion(newValue)
      }
      // Note: Removed toast notification for question length limit
    },
    [],
  )

  const sendQuestionDisabled = disabled || !question.trim()

  return (
    <VStack spacing={0} width='100%'>
      <Box
        borderWidth='1px'
        borderRadius='lg'
        p={0.5}
        bg={inputBg}
        borderColor={borderColor}
        position='relative'
        boxShadow='lg'
        _hover={{
          boxShadow: 'xl',
          borderColor: 'primary',
        }}
        transition='all 0.2s'
        width='100%'
      >
        <Flex position='relative'>
          <Textarea
            ref={textareaRef}
            variant='unstyled'
            placeholder={placeholder || t('chat.placeholder')}
            value={question}
            onChange={onQuestionChange}
            onKeyDown={onEnterPress}
            onFocus={onTyping}
            onBlur={onStopTyping}
            isDisabled={disabled}
            tabIndex={currentAgent ? 0 : -1}
            autoFocus={true}
            px={4}
            pt={2}
            pb={0}
            pr={24} // Add padding on the right to prevent text from going under buttons
            fontSize='15px'
            bg={inputBg}
            color={textColor}
            minH='20px'
            height='20px'
            maxH='150px'
            resize='none'
            overflow='auto'
            lineHeight='1.2'
            _placeholder={{
              color: placeholderColor,
            }}
          />
          <Flex
            position='absolute'
            right={2}
            top='50%'
            transform='translateY(-50%)'
            gap={1}
            bg={inputBg} // Add background to buttons to ensure text doesn't show through
            px={1}
            alignItems='center'
            height='100%'
          >
            {/* Send button */}
            <IconButton
              aria-label={t('common.send')}
              icon={<Send size={16} />}
              size='sm'
              variant='ghost'
              isDisabled={sendQuestionDisabled}
              color='primary'
              opacity={sendQuestionDisabled ? 0.5 : 1}
              _hover={{
                bg: 'primaryScheme.50',
                opacity: 1,
              }}
              onClick={handleSend}
            />
          </Flex>
        </Flex>
      </Box>
    </VStack>
  )
}
